import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi, useApp, usePayment } from "../../hooks";
import { useEffect, useRef, useState } from "react";
import Localization from "../../localization";
import { Text } from "../../components";
import { SVGs } from "../../assets";
import { Enums } from "../../utils";
import APIs from "../../apis";

const useViewModel = () => {
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const modifyCardRef = useRef();
  const { execute } = useApi();
  const payment = usePayment();
  const { modal } = useApp();

  const state = JSON.parse(searchParams.get("state"));
  const params = JSON.parse(searchParams.get("params"));
  const isSubscriptionUpgrade = !!params?.uuid;

  useEffect(() => {
    if (!state && !params)
      navigate(`/${Enums.pagesNames.purchaseSubscription}`);
    else getPlans();
  }, []);

  const getPlans = async () => {
    const plans = await execute(
      () =>
        isSubscriptionUpgrade
          ? APIs.subscriptions.paymentPlans(params.uuid, { upgrade: true })
          : APIs.services.paymentPlans(params || {}),
      { setLoading }
    );
    setPlans(plans);
  };

  const upgradeSubscription = async (plan) => {
    try {
      await APIs.subscriptions.upgradePlan(params.uuid, {
        plan: plan.id,
      });
      modal.success({
        icon: SVGs.balloons,
        afterClose: () => navigate(-1),
        okText: Localization.general.done,
        title: Localization.general.upgrade_subscription,
        description: Localization.formatString(
          Localization.subscription_plans.upgrade_successful_desc,
          <Text weight={600} level="text" children={plan.title} />
        ),
      });
    } catch (error) {
      if (error?.response?.status === 402) {
        modal.confirm({
          icon: SVGs.thunderCloud,
          title: Localization.general.upgrade_subscription,
          okText: Localization.general.update_payment_card,
          description: Localization.subscription_plans.payment_failed_desc,
          onOk: (_) => {
            _.destroy();
            modifyCardRef.current.open({ plan });
          },
        });
      }
    }
  };

  const addSubscriptionPaymentCard = async (plan) => {
    await payment.cardSetup({
      successDelay: 7000,
      serviceName: plan?.service,
      serviceUuid: params?.uuid,
      servicePaymentPlan: plan?.period,
    });
    await upgradeSubscription(plan);
    modifyCardRef.current.close();
  };

  const calculateProration = async (plan) => {
    const proration = await APIs.subscriptions.planProration(params.uuid, {
      plan: plan.id,
    });
    modal.confirm({
      title: Localization.general.upgrade_subscription,
      okText: Localization.general.proceed,
      onOk: async (modal) => {
        await upgradeSubscription(plan);
        modal.destroy();
      },
      description: (
        <>
          <Text
            type="secondary"
            children={Localization.formatString(
              Localization.subscription_plans.proration_desc_0,
              <Text
                weight={600}
                level="text"
                children={`${proration.amount} kr`}
              />
            )}
          />
          <Text
            type="secondary"
            className="m-10-t"
            children={Localization.formatString(
              Localization.subscription_plans.proration_desc_1,
              plan.title,
              proration.nextAmount
            )}
          />
        </>
      ),
    });
  };

  const checkout = async (plan) => {
    if (isSubscriptionUpgrade) {
      await calculateProration(plan);
      return;
    }
    await payment.checkout({
      name: plan.period,
      service: plan.service,
      cancelUrl: window.location.href,
      ...state,
    });
  };

  return {
    refs: { modifyCard: modifyCardRef },
    loading,
    plans,
    checkout,
    addSubscriptionPaymentCard,
  };
};

export default useViewModel;
